import { useEffect, useState } from "react";
import { usePatchOrganizationResponsiblePerson } from "@/features-root-user/service/organization";
import { ExternalLink, User } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  PopoverForm,
  PopoverFormButton,
  PopoverFormSuccess,
} from "@/components/ui/popover-form";

type FormState = "idle" | "loading" | "success";

interface ResponsiblePersonFormProps {
  organizationId: string;
  person: string;
  popoverIdentifier: string;
  jiraTicket?: string;
}

export function ResponsiblePersonForm({
  organizationId,
  person,
  popoverIdentifier,
  jiraTicket,
}: ResponsiblePersonFormProps) {
  const [formState, setFormState] = useState<FormState>("idle");
  const [open, setOpen] = useState(false);
  const [formName, setFormName] = useState(person);
  const [ticketNumber, setTicketNumber] = useState(jiraTicket || "");
  const [savedTicketNumber, setSavedTicketNumber] = useState(jiraTicket || "");
  const updateMutation = usePatchOrganizationResponsiblePerson();

  const handleSubmit = () => {
    if (!formName) return;
    setFormState("loading");
    updateMutation.mutate(
      {
        id: organizationId,
        responsiblePerson: formName,
        jiraTicket: ticketNumber || undefined,
      },
      {
        onSuccess: () => {
          setFormState("success");
          setSavedTicketNumber(ticketNumber);
          toast.success("Responsible person updated successfully!");
          setTimeout(() => {
            setOpen(false);
            setFormState("idle");
          }, 1000);
        },
        onError: () => {
          toast.error("Failed to update responsible person");
          setFormState("idle");
        },
      },
    );
  };

  const getJiraTicketUrl = (ticketNumber: string) => {
    return `https://ecogyenergy.atlassian.net/jira/software/c/projects/ON/boards/12?selectedIssue=ON-${ticketNumber}`;
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div className="flex items-center gap-2">
      <PopoverForm
        title={popoverIdentifier}
        name={<span>{person}</span>}
        open={open}
        setOpen={setOpen}
        width="320px"
        height="250px"
        showCloseButton={formState !== "success"}
        showSuccess={formState === "success"}
        openChild={
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            className="flex h-full flex-col overflow-y-auto p-4"
          >
            <div className="flex-grow space-y-4">
              <div className="space-y-2">
                <label
                  htmlFor="name"
                  className="text-muted-foreground mb-1 block text-sm font-medium"
                >
                  Responsible Person
                </label>
                <div className="relative">
                  <Input
                    id="name"
                    name="name"
                    onFocus={(e) => e.target.select()}
                    placeholder="Name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    required
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <User className="text-muted-foreground size-4" />
                  </div>
                </div>
                <p className="text-muted-foreground text-xs tracking-tight">
                  Person responsible for the organization.
                </p>
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="ticketNumber"
                  className="text-muted-foreground mb-1 block text-sm font-medium"
                >
                  JIRA Ticket
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-muted-foreground text-sm">ON-</span>
                  </div>
                  <Input
                    id="ticketNumber"
                    name="ticketNumber"
                    type="number"
                    min="0"
                    className="pl-12"
                    placeholder="Ticket Number"
                    value={ticketNumber}
                    onChange={(e) => setTicketNumber(e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                  />
                </div>
                <p className="text-muted-foreground text-xs tracking-tight">
                  JIRA ticket number associated with this organization.
                </p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <PopoverFormButton
                loading={formState === "loading"}
                text="Update"
              />
            </div>
          </form>
        }
        successChild={
          <PopoverFormSuccess
            title="Successfully updated!"
            description="The person responsible for this organization has been updated."
          />
        }
      />
      {savedTicketNumber && (
        <a
          href={getJiraTicketUrl(savedTicketNumber)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary/80 flex items-center text-xs font-medium"
        >
          ON-{savedTicketNumber}
          <ExternalLink className="ml-1 size-3" />
        </a>
      )}
    </div>
  );
}
